import { useEffect, useMemo, useRef, useState } from "react";
import { Download, Eye } from "react-feather";
import { toast } from "react-toastify";
import {
  downloadAgreementTemplatePdf,
  getAgreementTemplateErrorMessage,
  renderAgreementTemplate,
} from "../../../api/agreementTemplateApi";
import Button from "../../input/Button";
import Dropdown from "../../input/Dropdown";
import Input from "../../input/Input";
import { FORMS, MODULES } from "../../../const";

// ---------- helpers ----------

/** Extract unique placeholder keys like {customer_name} from an HTML string */
const inferPlaceholders = (html = "") => {
  const matches = [...html.matchAll(/\{(\w+)\}/g)];
  const seen = new Set();
  return matches
    .filter(({ 1: key }) => (seen.has(key) ? false : seen.add(key)))
    .map(({ 1: key }) => ({ key, value: "" }));
};

// ---------- component ----------

const AgreementTemplateRenderPreview = ({ templates = [] }) => {
  const [dropdownValue, setDropdownValue] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [rows, setRows] = useState([]);
  const [compiled, setCompiled] = useState(null);
  const [isRendering, setIsRendering] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const previewRef = useRef(null);

  // Build dropdown options from the templates prop
  const templateOptions = useMemo(
    () =>
      templates.map((t) => ({
        key: t.agtId,
        value: t.agtId,
        text: `${t.agtTitle}${t.agtCode ? ` (${t.agtCode})` : ""}`,
      })),
    [templates]
  );

  // The full selected template object
  const selectedTemplate = useMemo(
    () => templates.find((t) => t.agtId === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  const handleSelectTemplate = () => {
    // explicitly reset first
    setSelectedTemplateId("");
    setRows([]);
    setCompiled(null);

    // load relevant template second to flush previous state
    setTimeout(() => {
      setSelectedTemplateId(dropdownValue);
    }, 50);
  };

  const handleClear = () => {
    setDropdownValue("");
    setSelectedTemplateId("");
    setRows([]);
    setCompiled(null);
  };

  // Auto-detect placeholders whenever the template changes
  useEffect(() => {
    if (!selectedTemplate) {
      setRows([]);
      setCompiled(null);
      return;
    }
    const detected = inferPlaceholders(selectedTemplate.agtTemplate || "");
    setRows(detected);
    setCompiled(null);
  }, [selectedTemplate]);

  const setRowValue = (index, value) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, value } : row))
    );
    setCompiled(null); // clear stale preview when values change
  };

  const getPlaceholdersMap = () => {
    return rows.reduce((acc, row) => {
      acc[row.key] = row.value;
      return acc;
    }, {});
  };

  // Compile via backend + scroll to preview
  const handleView = async () => {
    if (!selectedTemplate) return;

    setIsRendering(true);
    try {
      const response = await renderAgreementTemplate({
        agtId: selectedTemplate.agtId,
        placeholders: getPlaceholdersMap(),
      });
      setCompiled(response.renderedTemplate);
      setTimeout(() => {
        previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (error) {
      toast.error(getAgreementTemplateErrorMessage(error), {
        autoClose: 5000,
        containerId: "Main",
      });
    } finally {
      setIsRendering(false);
    }
  };

  // Download PDF via Backend API
  const handleDownload = async () => {
    if (!selectedTemplate) return;

    setIsDownloading(true);
    try {
      const response = await downloadAgreementTemplatePdf({
        agtId: selectedTemplate.agtId,
        placeholders: getPlaceholdersMap(),
        watermark: true,
      });
      
      const url = window.URL.createObjectURL(new Blob([response], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${"Agreement"}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download PDF from server.", {
        autoClose: 5000,
        containerId: "Main",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const canRender = !!selectedTemplate;

  return (
    <div className="ui form" style={{ padding: "1rem" }}>
      <h4 className="ui dividing header">Render Preview (PDF)</h4>

      {/* Template selector controls */}
      <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
        <div style={{ flex: 1 }}>
          <Dropdown
            required={true}
            label="Template"
            placeholder="Select a template"
            options={templateOptions}
            value={dropdownValue}
            onChange={(e, data) => setDropdownValue(data.value)}
          />
        </div>
        <Button
          className="ui primary button"
          label="Select Template"
          onClick={handleSelectTemplate}
          disabled={!dropdownValue}
        />
        <Button
          className="ui button"
          label="Clear"
          onClick={handleClear}
          disabled={!dropdownValue && !selectedTemplateId}
        />
      </div>

      {/* Raw template body (read-only) */}
      {selectedTemplate && (
        <div className="field" style={{ marginTop: "1rem" }}>
          <label>Template Body (raw)</label>
          <div
            dangerouslySetInnerHTML={{
              __html:
                selectedTemplate.agtTemplate?.trim().length > 0
                  ? selectedTemplate.agtTemplate
                  : "<p><em>No template body stored.</em></p>",
            }}
            style={{
              border: "1px solid #ddd",
              borderRadius: "4px",
              padding: "12px 16px",
              background: "#fafafa",
              minHeight: "80px",
              maxHeight: "280px",
              overflowY: "auto",
              lineHeight: 1.6,
            }}
          />
        </div>
      )}

      {/* Placeholder filler */}
      {selectedTemplate && rows.length > 0 && (
        <div className="field" style={{ marginTop: "1rem" }}>
          <label>Fill Placeholders</label>
          <div
            style={{
              border: "1px solid #ddd",
              borderRadius: "4px",
              padding: "10px 14px",
              background: "#fff",
            }}
          >
            <div className="two fields">
              <div className="field">
                <small style={{ fontWeight: 600, color: "#555" }}>
                  Placeholder
                </small>
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <small style={{ fontWeight: 600, color: "#555" }}>Value</small>
              </div>
            </div>

            {rows.map((row, index) => (
              <div key={row.key} className="two fields" >
                <div className="two field">
                  <div
                    style={{
                      background: "#f3f4f6",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      padding: "6px 10px",
                      fontFamily: "monospace",
                      fontSize: "13px",
                      color: "#374151",
                      width: "100%",
                    }}
                  >
                    {`{${row.key}}`}
                  </div>
                </div>

                <Input
                  placeholder={`Enter ${row.key}`}
                  value={row.value}
                  onChange={(e) => setRowValue(index, e.target.value)}
                  style={{ marginTop: -25 }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedTemplate && rows.length === 0 && (
        <div className="ui info message" style={{ marginTop: "1rem" }}>
          No placeholders detected in this template. Click <strong>View</strong> to
          fetch the rendered version from the server.
        </div>
      )}

      {/* Action buttons */}
      {canRender && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
            marginTop: "1rem",
          }}
        >
          <Button
            label="View"
            Icon={Eye}
            onClick={handleView}
            isLoading={isRendering}
            form={FORMS.AGREEMENT_TEMPLATE?.PREVIEW?.form}
            module={MODULES.Master}
            // authenticated={true}
          />
          <Button
            className="ui primary button"
            label="Download PDF"
            Icon={Download}
            onClick={handleDownload}
            isLoading={isDownloading}
            form={FORMS.AGREEMENT_TEMPLATE?.PREVIEW?.form}
            module={MODULES.Master}
            // authenticated={true}
          />
        </div>
      )}

      {/* Compiled preview inline */}
      {compiled !== null && (
        <div ref={previewRef} style={{ marginTop: "1.5rem" }}>
          <div className="ui dividing header" style={{ fontSize: "1rem" }}>
            Preview
          </div>
          <div
            dangerouslySetInnerHTML={{ __html: compiled }}
            style={{
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              padding: "20px 24px",
              background: "#fff",
              lineHeight: 1.7,
              minHeight: "100px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AgreementTemplateRenderPreview;
